/**
 * In-App Feedback Widget
 * Techy, professional feedback submission interface
 * Perfect for collecting user feedback directly from the app
 */

import React, { useState, useEffect } from 'react';
import { MessageCircle, Send, Star, AlertCircle, CheckCircle2, X, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { useFeedbackWidget } from '@/hooks/useFeedbackWidget';
import { cn } from '@/lib/utils';

interface FeedbackWidgetProps {
  position?: 'bottom-right' | 'bottom-left';
  theme?: 'light' | 'dark';
}

const FEEDBACK_TYPES = [
  { value: 'bug_report', label: 'Bug Report', emoji: '🐛' },
  { value: 'feature_request', label: 'Feature Request', emoji: '✨' },
  { value: 'usability', label: 'Usability', emoji: '🎯' },
  { value: 'performance', label: 'Performance', emoji: '⚡' },
  { value: 'compliance', label: 'Compliance', emoji: '✅' },
];

const CATEGORIES = [
  { value: 'ui_ux', label: 'UI/UX', icon: '🎨' },
  { value: 'feature', label: 'Feature', icon: '🚀' },
  { value: 'integration', label: 'Integration', icon: '🔗' },
  { value: 'security', label: 'Security', icon: '🔒' },
  { value: 'performance', label: 'Performance', icon: '⚡' },
  { value: 'documentation', label: 'Documentation', icon: '📚' },
];

const SENTIMENTS = [
  { value: 'negative', emoji: '😟', label: 'Frustrated' },
  { value: 'neutral', emoji: '😐', label: 'Neutral' },
  { value: 'positive', emoji: '😊', label: 'Happy' },
];

/**
 * Confetti animation effect
 */
const Confetti = ({ isActive }: { isActive: boolean }) => {
  const particles = Array.from({ length: 50 });

  if (!isActive) return null;

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      {particles.map((_, i) => {
        const delay = Math.random() * 0.2;
        const left = Math.random() * 100;
        const duration = 2 + Math.random() * 1;

        return (
          <div
            key={i}
            className="absolute animate-pulse"
            style={{
              left: `${left}%`,
              top: '-10px',
              fontSize: `${12 + Math.random() * 12}px`,
              animation: `confetti ${duration}s linear ${delay}s forwards`,
            }}
          >
            {'🎉'[Math.floor(Math.random() * 1)]}
          </div>
        );
      })}
      <style>{`
        @keyframes confetti {
          0% {
            opacity: 1;
            transform: translateY(0) rotate(0deg);
          }
          100% {
            opacity: 0;
            transform: translateY(100vh) rotate(360deg);
          }
        }
      `}</style>
    </div>
  );
};

export function FeedbackWidget({ position = 'bottom-right', theme = 'dark' }: FeedbackWidgetProps) {
  const {
    isOpen,
    setIsOpen,
    isSubmitting,
    submitted,
    error,
    handleSubmit,
  } = useFeedbackWidget();

  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    sentiment: 'neutral' as const,
    feedback_type: 'feature_request',
    category: 'feature',
    title: '',
    description: '',
  });
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);

  const handleOpen = () => {
    setStep(1);
    setFormData({
      sentiment: 'neutral',
      feedback_type: 'feature_request',
      category: 'feature',
      title: '',
      description: '',
    });
    setRating(0);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleNext = () => {
    if (step === 1 && !formData.sentiment) {
      return;
    }
    if (step === 2 && !formData.feedback_type) {
      return;
    }
    if (step === 2 && !formData.category) {
      return;
    }
    setStep(step + 1);
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmitForm = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }

    await handleSubmit({
      ...formData,
      sentiment: formData.sentiment,
      context: {
        page: window.location.pathname,
        rating,
        timestamp: new Date().toISOString(),
      },
    });
  };

  const positionClasses = position === 'bottom-right'
    ? 'bottom-6 right-6'
    : 'bottom-6 left-6';

  return (
    <>
      <Confetti isActive={submitted} />

      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={handleOpen}
          className={cn(
            'fixed z-40 rounded-full p-3 shadow-2xl transition-all duration-300 hover:shadow-2xl',
            positionClasses,
            'bg-gradient-to-br from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700',
            'hover:scale-110 active:scale-95',
            'group cursor-pointer'
          )}
          title="Send us your feedback"
        >
          <MessageCircle className="w-6 h-6 text-white group-hover:rotate-12 transition-transform" />

          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-purple-500 opacity-0 group-hover:opacity-20 blur-lg transition-opacity" />

          {/* Pulsing ring */}
          <div className="absolute inset-0 rounded-full border-2 border-purple-300 opacity-0 group-hover:opacity-30 animate-pulse" />
        </button>
      )}

      {/* Modal Dialog */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="max-w-md sm:max-w-lg border-0 shadow-2xl bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 text-white">
          {/* Header */}
          <DialogHeader className="space-y-2 border-b border-slate-700 pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-xl font-semibold flex items-center gap-2">
                <span className="text-2xl">💬</span>
                Share Your Feedback
              </DialogTitle>
              <button
                onClick={handleClose}
                className="rounded-full p-1 hover:bg-slate-700 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-slate-400">Help us improve Aegis</p>
          </DialogHeader>

          {/* Success State */}
          {submitted && (
            <div className="py-12 flex flex-col items-center gap-4 text-center">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-xl opacity-50 animate-pulse" />
                <CheckCircle2 className="w-16 h-16 text-emerald-500 relative" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-white mb-1">Thanks for your feedback!</h3>
                <p className="text-sm text-slate-400">
                  Your insights help us build a better product.
                </p>
              </div>
              <div className="text-4xl animate-bounce">🎉</div>
            </div>
          )}

          {/* Error State */}
          {error && !submitted && (
            <div className="flex items-start gap-3 p-3 rounded-lg bg-red-500/10 border border-red-500/20 mb-4">
              <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-red-300">{error}</div>
            </div>
          )}

          {/* Content */}
          {!submitted && (
            <div className="form-content space-y-6">
              {/* Step 1: Sentiment */}
              {step === 1 && (
                <div className="space-y-4 animate-in">
                  <div>
                    <p className="text-sm font-medium text-slate-300 mb-3">
                      How are you feeling about this?
                    </p>
                    <div className="flex gap-4 justify-around">
                      {SENTIMENTS.map(sentiment => (
                        <button
                          key={sentiment.value}
                          onClick={() => setFormData({ ...formData, sentiment: sentiment.value as any })}
                          className={cn(
                            'flex flex-col items-center gap-2 py-3 px-4 rounded-lg transition-all duration-200',
                            formData.sentiment === sentiment.value
                              ? 'bg-gradient-to-br from-purple-600 to-blue-600 scale-110'
                              : 'bg-slate-800 hover:bg-slate-700'
                          )}
                        >
                          <span className="text-4xl">{sentiment.emoji}</span>
                          <span className="text-xs text-slate-300">{sentiment.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Star Rating */}
                  <div>
                    <p className="text-sm font-medium text-slate-300 mb-3">
                      Rate your experience
                    </p>
                    <div className="flex gap-3">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setRating(i + 1)}
                          onMouseEnter={() => setHoverRating(i + 1)}
                          onMouseLeave={() => setHoverRating(0)}
                          className="transition-transform hover:scale-125"
                        >
                          <Star
                            className={cn(
                              'w-8 h-8 transition-all',
                              (hoverRating || rating) > i
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-slate-700'
                            )}
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 2: Type & Category */}
              {step === 2 && (
                <div className="space-y-4 animate-in">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">
                      Feedback Type
                    </label>
                    <Select
                      value={formData.feedback_type}
                      onValueChange={(value) =>
                        setFormData({ ...formData, feedback_type: value })
                      }
                    >
                      <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        {FEEDBACK_TYPES.map(type => (
                          <SelectItem key={type.value} value={type.value}>
                            <span className="flex items-center gap-2">
                              {type.emoji} {type.label}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">
                      Category
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {CATEGORIES.map(cat => (
                        <button
                          key={cat.value}
                          onClick={() => setFormData({ ...formData, category: cat.value })}
                          className={cn(
                            'flex items-center gap-2 py-2 px-3 rounded-lg text-sm transition-all',
                            formData.category === cat.value
                              ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white'
                              : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                          )}
                        >
                          <span>{cat.icon}</span>
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Step 3: Details */}
              {step === 3 && (
                <div className="space-y-4 animate-in">
                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">
                      Title
                    </label>
                    <Input
                      placeholder="Brief summary of your feedback"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500"
                      maxLength={100}
                    />
                    <p className="text-xs text-slate-500 mt-1">{formData.title.length}/100</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-slate-300 mb-2 block">
                      Description
                    </label>
                    <Textarea
                      placeholder="Share more details about your feedback..."
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-slate-800 border-slate-700 text-white placeholder-slate-500 resize-none"
                      rows={4}
                      maxLength={500}
                    />
                    <p className="text-xs text-slate-500 mt-1">{formData.description.length}/500</p>
                  </div>

                  {/* Summary */}
                  <div className="p-3 rounded-lg bg-slate-800 border border-slate-700 space-y-2">
                    <p className="text-xs font-medium text-slate-400">Summary</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-400">Sentiment:</span>
                        <span className="text-white">
                          {SENTIMENTS.find(s => s.value === formData.sentiment)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Type:</span>
                        <span className="text-white">
                          {FEEDBACK_TYPES.find(t => t.value === formData.feedback_type)?.label}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-400">Category:</span>
                        <span className="text-white">
                          {CATEGORIES.find(c => c.value === formData.category)?.label}
                        </span>
                      </div>
                      {rating > 0 && (
                        <div className="flex justify-between">
                          <span className="text-slate-400">Rating:</span>
                          <span className="text-yellow-400">{'⭐'.repeat(rating)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation */}
              <div className="flex gap-3 pt-4">
                {step > 1 && (
                  <Button
                    variant="outline"
                    onClick={handleBack}
                    className="flex-1 bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
                  >
                    Back
                  </Button>
                )}

                {step < 3 ? (
                  <Button
                    onClick={handleNext}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
                  >
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handleSubmitForm}
                    disabled={isSubmitting || !formData.title.trim() || !formData.description.trim()}
                    className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white disabled:opacity-50"
                  >
                    {isSubmitting ? 'Sending...' : 'Send Feedback'}
                    <Send className="w-4 h-4 ml-2" />
                  </Button>
                )}
              </div>

              {/* Progress Indicator */}
              <div className="flex gap-2 justify-center pt-2">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className={cn(
                      'h-1 rounded-full transition-all',
                      i < step
                        ? 'bg-gradient-to-r from-purple-600 to-blue-600 w-8'
                        : 'bg-slate-700 w-2'
                    )}
                  />
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default FeedbackWidget;
